import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useState} from 'react';

import {assert} from 'Asserts';
import HelpButton from 'help/HelpButton';
import Button, {ButtonStyle, ButtonSize} from 'ui-library/buttons/Button';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import {InputErrorMessageStyle, LABEL_STYLES, Size} from 'ui-library/inputs/InputConstants';
import TextField from 'ui-library/inputs/TextField';

import {isValidOligoDuplexOffset} from '../ComplexPolymerUtil';

export interface OligoDuplexOffsetFieldProps {
  offset: number | null;
  setOffset: (offset: number | null) => void;
  isEditable: boolean;
  forwardStrandLength?: number;
  reverseStrandLength?: number;
  // Whether we allow edits via typing in the text box, or just the -/+ buttons.
  textBoxEditable?: boolean;
}

export default function OligoDuplexOffsetField({
  offset,
  setOffset,
  isEditable,
  forwardStrandLength,
  reverseStrandLength,
  textBoxEditable,
}: OligoDuplexOffsetFieldProps): JSX.Element {
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setError(null);
  }, [offset]);

  const validateOffsetAndSetError = (newOffset: number): boolean => {
    if (
      forwardStrandLength &&
      reverseStrandLength &&
      !isValidOligoDuplexOffset(newOffset, forwardStrandLength, reverseStrandLength)
    ) {
      setError('Nucleotide strands must overlap');
      return false;
    }
    return true;
  };

  const parseAndSetOffset = (newOffset: string): void => {
    if (!newOffset) {
      setOffset(null);
    }

    const intValue = parseInt(newOffset, 10);
    if (Number.isNaN(intValue)) {
      setError('Offset must be a number');
    } else if (validateOffsetAndSetError(intValue)) {
      setOffset(intValue);
    }
  };

  return (
    <div>
      <span>
        <FieldLabel label="Forward offset" />{' '}
        <HelpButton renderContent={() => "Adjust the 5' offset for the forward strand"} />
      </span>
      <div className={css(styles.offsetField)}>
        <Button
          buttonStyle={ButtonStyle.TERTIARY_GRAY}
          icon="minus"
          size={ButtonSize.SMALL}
          disabled={!isEditable || offset == null}
          onClick={(e) => {
            assert(offset != null, 'Expected offset to be non-null');
            if (validateOffsetAndSetError(offset - 1)) {
              setOffset(offset - 1);
            }
          }}
          dataTestAttrs={{component: 'OligoDuplexOffsetField', element: 'minusButton'}}
        />
        <div className={css(styles.textField)}>
          <TextField
            value={offset == null ? '' : offset.toString()}
            size={Size.SMALL}
            labelStyle={LABEL_STYLES.HIDDEN}
            /* TODO (BNCH-95178): Instead of disabling the text field when updating existing duplexes, we should add a save button. */
            disabled={!isEditable || !(textBoxEditable ?? true)}
            onChange={(e) => {
              parseAndSetOffset(e.target.value);
            }}
            error={
              error != null
                ? {
                    message: error,
                    style: InputErrorMessageStyle.ERROR,
                  }
                : null
            }
            dataTestAttrs={{component: 'OligoDuplexOffsetField', element: 'textField'}}
          />
        </div>

        <Button
          buttonStyle={ButtonStyle.TERTIARY_GRAY}
          icon="plus"
          size={ButtonSize.SMALL}
          disabled={!isEditable || offset == null}
          onClick={(e) => {
            assert(offset != null, 'Expected offset to be non-null');
            if (validateOffsetAndSetError(offset + 1)) {
              setOffset(offset + 1);
            }
          }}
          dataTestAttrs={{component: 'OligoDuplexOffsetField', element: 'plusButton'}}
        />
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  textField: {
    width: '75px',
  },
  offsetField: {
    width: '140px',
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'row',
  },
  textInput: {
    textAlign: 'right',
  },
});
